import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import MSTeamsLogo from '@atlassian/jira-icons/src/ui/ms-teams-logomark';
import SlackLogo from '@atlassian/jira-icons/src/ui/slack-logomark';
import { useIntl } from '@atlassian/jira-intl';
import msTeamsImage from '../../../assets/msteamlogo.svg';
import slackImage from '../../../assets/slack.svg';
import { SLACK_APP_TYPE, type AppType } from '../../../common/constants';
import { AppIcon } from '../../../common/ui';
import { ExpandButton } from '../../../common/ui/expand-button';
import { UpgradePlanContent } from '../../../common/ui/upgrade-plan-content';
import { useChatOpsEntitlement } from '../../../services/main';
import { messages, getWorkspaceConnectionEnabled } from './messages';
import { SlackExpandButton } from './slack-expand-button';

type Props = {
	appType: AppType;
	enabledWorkspacesCount: number;
	isExpanded: boolean;
	hasWorkspacesConnected: boolean;
	setIsExpanded: (newValue: boolean) => void;
};

export const WorkspaceConnectionSummary = ({
	appType,
	enabledWorkspacesCount,
	isExpanded,
	hasWorkspacesConnected,
	setIsExpanded,
}: Props) => {
	const { formatMessage } = useIntl();
	const image = appType === SLACK_APP_TYPE ? slackImage : msTeamsImage;
	const AppLogo = appType === SLACK_APP_TYPE ? SlackLogo : MSTeamsLogo;
	const sectionHeading =
		appType === SLACK_APP_TYPE
			? messages.slackWorkspaceConnectionSectionHeading
			: messages.msteamsWorkspaceConnectionSectionHeading;
	const [isPaymentPopperOpen, setIsPaymentPopperOpen] = useState(false);
	const [{ chatOpsEntitlementEnabled }] = useChatOpsEntitlement();
	const isChatOpsDisabled =
		!hasWorkspacesConnected && !chatOpsEntitlementEnabled && ff('chatops-pp-banners_1o319');

	return (
		<>
			<Inline spread="space-between" alignBlock="center" xcss={InlineStyle}>
				<Inline alignBlock="center" spread="space-between">
					{ff('jfe-asset-consolidation-itsm-agni_po996') ? (
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
						<Box xcss={AppLogoContainerStyle(isChatOpsDisabled)}>
							<AppLogo label={formatMessage(sectionHeading)} />
						</Box>
					) : (
						<AppIcon
							src={image}
							role="img"
							title={formatMessage(sectionHeading)}
							color={isChatOpsDisabled ? token('color.icon.disabled', '#BFDBF847') : undefined}
						/>
					)}
					{appType === SLACK_APP_TYPE ? (
						<SlackTextStyle isChatOpsDisabled={isChatOpsDisabled}>
							{formatMessage(sectionHeading)}
						</SlackTextStyle>
					) : (
						<MsTeamsTextStyle isChatOpsDisabled={isChatOpsDisabled}>
							{formatMessage(sectionHeading)}
						</MsTeamsTextStyle>
					)}
					{enabledWorkspacesCount > 0 && (
						<Lozenge appearance="success">
							{formatMessage(getWorkspaceConnectionEnabled(appType), {
								enabledWorkspacesCount,
							})}
						</Lozenge>
					)}
				</Inline>
				{isChatOpsDisabled ? (
					<Popup
						isOpen={isPaymentPopperOpen}
						onClose={() => setIsPaymentPopperOpen(false)}
						placement="bottom-end"
						content={() => (
							<Box xcss={upgradePopperStyles}>
								<UpgradePlanContent />
							</Box>
						)}
						trigger={(triggerProps) => (
							// eslint-disable-next-line react/jsx-props-no-spreading
							<Box {...triggerProps}>
								<ExpandButton
									isDisabled={!chatOpsEntitlementEnabled}
									label={formatMessage(messages.workspaceConnectionExpandButtonLabel)}
									onClick={() => {
										setIsPaymentPopperOpen(!chatOpsEntitlementEnabled && !isPaymentPopperOpen);
										setIsExpanded(chatOpsEntitlementEnabled && !isExpanded);
									}}
									isExpanded={chatOpsEntitlementEnabled && isExpanded}
								/>
							</Box>
						)}
					/>
				) : (
					<SlackExpandButton
						label={formatMessage(messages.workspaceConnectionExpandButtonLabel)}
						onClick={() => setIsExpanded(!isExpanded)}
						isExpanded={isExpanded}
					/>
				)}
			</Inline>
		</>
	);
};

const InlineStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	paddingRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MsTeamsTextStyle = styled.h4<{ isChatOpsDisabled: boolean }>({
	margin: `0 ${token('space.250', '2px')} 0 ${token('space.150', '12px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	color: ({ isChatOpsDisabled }) =>
		isChatOpsDisabled ? token('color.text.disabled') : token('color.text', 'inherit'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SlackTextStyle = styled.h4<{ isChatOpsDisabled: boolean }>({
	margin: `0 ${token('space.300', '24px')} 0 ${token('space.150', '12px')}`,
	paddingRight: token('space.1000', '80px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	color: ({ isChatOpsDisabled }) =>
		isChatOpsDisabled ? token('color.text.disabled') : token('color.text', 'inherit'),
});

const AppLogoContainerStyle = (isChatOpsDisabled: boolean) =>
	xcss({
		width: token('space.300', '24px'),
		height: token('space.300', '24px'),
		marginLeft: 'space.150',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		opacity: isChatOpsDisabled ? 0.5 : 1,
	});

const upgradePopperStyles = xcss({ padding: 'space.150', width: '300px' });
